import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, productImage, hasCover } from "../lib/brand";
import { useCart } from "../lib/cart";
import ProtectedImage from "../components/ProtectedImage";
import { ArrowLeft, Check, Plus, ShoppingBag } from "lucide-react";

const PREVIEW_SECONDS = 15;

function PreviewAudio({ productId, hasAudio }) {
  const ref = useRef(null);
  if (!hasAudio) return null;
  const url = `${API}/preview/audio/${productId}`;
  return (
    <div data-testid="product-audio" className="mb-8 p-4 border border-[#1A2B20] bg-[#09120C]">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">15-second preview</div>
      <audio
        ref={ref}
        src={url}
        controls
        className="w-full"
        controlsList="nodownload noplaybackrate"
        onTimeUpdate={(e) => {
          if (e.currentTarget.currentTime >= PREVIEW_SECONDS) {
            e.currentTarget.pause();
            e.currentTarget.currentTime = 0;
          }
        }}
      />
      <p className="mt-2 text-[10px] text-[#5C7064] leading-relaxed">
        Full uncut track is delivered after purchase.
      </p>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { add, has } = useCart();

  useEffect(() => {
    axios.get(`${API}/products/${id}`)
      .then((r) => setProduct(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-40 px-6 text-[#95A89C]">Loading…</div>;
  if (!product) return <div className="pt-40 px-6 text-white">Not found</div>;

  const inCart = has(product.id);
  const hasAudio = product.type === "music" && !!product.audio_path;

  return (
    <div data-testid="product-detail-page" className="pt-32 pb-24 max-w-7xl mx-auto px-6 lg:px-12">
      <Link to={`/${product.type === "drawing" ? "drawings" : product.type}`} className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#95A89C] hover:text-[#279153] mb-10">
        <ArrowLeft className="w-4 h-4" /> Back to {product.type}
      </Link>

      <div className="grid lg:grid-cols-2 gap-12">
        <ProtectedImage
          src={productImage(product)}
          alt={product.title}
          aspectClass="aspect-square"
          watermarkScale={0.5}
          badgeHeightClass="h-9"
          className="border border-[#1A2B20]"
          imgClassName={hasCover(product) ? "" : "!object-contain p-20 opacity-70"}
        />

        <div>
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-4">{product.type}</div>
          <h1 data-testid="product-title" className="font-serif-display text-5xl text-white leading-tight mb-6">{product.title}</h1>
          <p data-testid="product-description" className="text-base text-[#95A89C] leading-relaxed mb-8">{product.description || "A new piece from the studio."}</p>

          <PreviewAudio productId={product.id} hasAudio={hasAudio} />

          <div className="flex items-end justify-between border-t border-[#1A2B20] pt-8 mb-8">
            <div>
              <div className="text-xs text-[#5C7064] tracking-wide">Price</div>
              <div data-testid="product-price" className="font-serif-display text-4xl text-white">${Number(product.price).toFixed(2)} <span className="text-sm text-[#5C7064]">USD</span></div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              data-testid="product-add-to-cart"
              disabled={inCart}
              onClick={() => add(product)}
              className={`flex-1 flex items-center justify-center gap-3 px-6 py-4 text-xs tracking-[0.3em] uppercase transition-colors ${
                inCart ? "bg-[#1A2B20] text-[#95A89C]" : "bg-[#1E7040] hover:bg-[#165932] text-white"
              }`}
            >
              {inCart ? <><Check className="w-4 h-4" /> In cart</> : <><Plus className="w-4 h-4" /> Add to cart</>}
            </button>
            <Link
              to="/cart"
              data-testid="product-go-to-cart"
              className="flex items-center justify-center gap-3 px-6 py-4 border border-white/20 text-white text-xs tracking-[0.3em] uppercase hover:border-[#279153] hover:text-[#279153]"
            >
              <ShoppingBag className="w-4 h-4" /> Cart
            </Link>
          </div>

          <div className="mt-10 p-5 bg-[#09120C] border border-[#1A2B20]">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Authenticity</div>
            <p className="text-sm text-[#95A89C] leading-relaxed">
              All previews are watermarked with the Grá Celtic-knot mark. After purchase you receive an un-watermarked, high-resolution release direct from the artist.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
